import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import GettingStarted from '@/components/marketing/GettingStarted';
import CommonQuestions from '@/components/marketing/CommonQuestions';
import ClosedLoop from '@/components/marketing/ClosedLoop';
import { CBO_COUNT, LANGUAGES, contactHref } from '@/components/marketing/siteFacts';
import { ArrowRight, Building2, Search, Send, CheckCircle, Users } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

interface NetworkStats {
  totalProviders: number;
  totalOrganizations: number;
  statesCovered: number;
  serviceCategories: number;
  topServices?: { service: string; count: number }[];
}

/**
 * For hospitals & health systems — the people who send the referral.
 *
 * The nav and the page title always said hospitals, but the body used to be a
 * pitch for community organizations to join the network. That material now
 * lives on the community organizations page; this one is only about sending a
 * patient to real help and finding out whether they received it.
 */
export default function ProvidersSolutionsPage() {
  usePageMeta(SEO_ROUTES['/solutions/providers']);

  const [stats, setStats] = useState<NetworkStats | null>(null);

  useEffect(() => {
    fetch('/provider-api/stats')
      .then(res => res.json())
      .then(data => {
        if (data.total_providers) {
          setStats({
            totalProviders: data.total_providers,
            totalOrganizations: Object.keys(data.by_organization_type || {}).length,
            statesCovered: Object.keys(data.by_state || {}).length,
            serviceCategories: 0
          });
        }
      })
      .catch(() => {
        fetch(`${API_BASE}/api/public/network-stats`)
          .then(res => res.json())
          .then(data => setStats(data))
          .catch(() => setStats(null));
      });
  }, []);

  const providerCount = stats
    ? (stats.totalProviders >= 1000000 ? (stats.totalProviders / 1000000).toFixed(1) + 'M+' : Math.floor(stats.totalProviders / 1000).toLocaleString() + 'K+')
    : '1.7M+';

  const discharge = [
    {
      icon: Search,
      title: 'Find help that can take the patient',
      description: 'Search for services near the patient, open to them and able to help in their language — not a printed list of phone numbers.',
    },
    {
      icon: Send,
      title: 'Send the referral from the same screen',
      description: 'Coverage is checked first, so the referral does not fail at the front desk. The receiving organization gets it in its own inbox.',
    },
    {
      icon: CheckCircle,
      title: 'Hear what happened',
      description: 'Received, scheduled, delivered or not reached — the confirmation comes back to the person who referred, without anyone chasing it.',
    },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="bg-paper border-b border-v2border py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 bg-v2accent-soft rounded-full mb-6">
              <Building2 className="w-5 h-5 text-v2accent mr-2" />
              <span className="text-ink font-medium">For Hospitals &amp; Health Systems</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-ink mb-6">
              Send a referral.<br />
              <span className="text-v2accent">Find out what happened.</span>
            </h1>
            <p className="text-xl text-ink-500 mb-4">
              A patient leaves needing food, stable housing or a ride to the next
              appointment. Today the referral goes out and nothing comes back — and the
              first news of how it went is often the next emergency department visit.
            </p>
            <p className="text-base text-ink-400 mb-8">
              WeaveIn shows your team which community organizations are near the patient
              and able to help, sends the referral, and tells you when the person was
              actually served. Searchable across a directory of {providerCount} U.S. health
              and social service providers.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to={contactHref('general', 'Hospital or health system')}
                className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors"
              >
                Talk to us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/platform"
                className="inline-flex items-center px-8 py-4 bg-v2accent-soft text-ink text-lg font-semibold rounded-lg hover:bg-ink/20 transition-colors"
              >
                See how it works
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* For the discharge team */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-ink dark:text-white mb-10 text-center">
            For the discharge nurse and the case manager
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {discharge.map((d) => (
              <div key={d.title} className="rounded-xl p-6 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                <d.icon className="w-8 h-8 text-v2accent mb-4" aria-hidden="true" />
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">{d.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{d.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ClosedLoop />

      {/* Network stats */}
      <section className="py-14 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-ink dark:text-white mb-2">{providerCount}</div>
              <div className="text-gray-600 dark:text-gray-400">Providers in the directory</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-ink dark:text-white mb-2">{CBO_COUNT}</div>
              <div className="text-gray-600 dark:text-gray-400">Community organizations on the platform</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-ink dark:text-white mb-2">{LANGUAGES}</div>
              <div className="text-gray-600 dark:text-gray-400">Languages for patient outreach</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pointer for community organizations */}
      <section className="py-10 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/solutions/hubs"
            className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:border-v2accent transition-colors"
          >
            <Users className="w-8 h-8 text-v2accent shrink-0" aria-hidden="true" />
            <div className="flex-1">
              <p className="font-bold text-gray-900 dark:text-white">Are you a community organization that receives referrals?</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Joining the network, receiving referrals and getting paid by each funder are on the community organizations page.
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-ink" aria-hidden="true" />
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-paper border-b border-v2border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-ink mb-6">
            Close the loop on your next discharge
          </h2>
          <p className="text-xl text-ink-500 mb-10">
            Tell us where referrals go quiet today. We will show you what the confirmation looks like when it comes back.
          </p>
          <Link
            to={contactHref('general', 'Hospital or health system')}
            className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors shadow-lg"
          >
            Talk to us
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>

      <CommonQuestions
        questions={[
          {
            q: 'Our EHR already tracks referrals. Why add this?',
            a: 'Your EHR can see what happens inside your own walls. It has no view of whether the food bank served the patient, whether the shelter placed them, or whether they were reached at all — because those organizations are not in it. That is the gap this closes.',
          },
          {
            q: 'Does this add work for the discharge team?',
            a: 'It replaces a phone call and a printed list. The nurse searches for services that are near the patient and able to help, sends the referral from the same screen, and is told what happened without chasing it.',
          },
        ]}
      />

      <GettingStarted />

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
}
